// adapter แหล่งไฟล์ของ PdfEditor — บอกว่าโหลดต้นฉบับจากไหน บันทึกไปที่ไหน มีเมนูบันทึกอะไรบ้าง
// รูปแบบ: { uid, name, downloadName, allowMerge, closeAfterSave, saveOptions: [{ mode, label, icon, confirm? }],
//          load() -> { bytes, restore?(pages, nextId) } | { error },  save(mode, { bytes, pages }) -> { status, message, doneMessage } }
import { GET, POST, PUT } from "../CustomComponent";
import { bytesToDataUrl, dataUrlToBytes } from "./imageUtils";
import { buildLayoutV2, legacyLayoutToPages, restoreLayoutV2 } from "./layout";

const pdfBase = (name) => String(name || "document").replace(/\.pdf$/i, "");

/* ---------- ไฟล์ใน drive ---------- */

// file: { uid, name, folder_uid }
export function driveSource(file) {
  const editedName = `${pdfBase(file.name)}_edited.pdf`;
  return {
    uid: file.uid,
    name: file.name,
    downloadName: editedName,
    allowMerge: true,
    saveOptions: [
      { mode: "new", label: "บันทึกเป็นไฟล์ใหม่", icon: "fa-file-circle-plus" },
      {
        mode: "overwrite", label: "บันทึกทับไฟล์เดิม", icon: "fa-file-pen",
        confirm: { title: "บันทึกทับไฟล์เดิม", text: `ไฟล์ "${file.name}" จะถูกแทนที่ด้วยเวอร์ชันที่แก้ไขนี้ และย้อนกลับไม่ได้ ต้องการดำเนินการต่อ?`, ok: "บันทึกทับ" },
      },
    ],

    async load() {
      const result = await GET(`v1/admin/drive/files/${file.uid}/content`, undefined, undefined, { silent: true });
      if (!result?.status || !result.data?.file_base64) return { error: result?.message || "เปิดไฟล์ไม่สำเร็จ" };
      return { bytes: dataUrlToBytes(result.data.file_base64) };
    },

    async save(mode, { bytes }) {
      const dataUrl = await bytesToDataUrl(bytes, "application/pdf");
      const result = mode === "overwrite"
        ? await PUT(`v1/admin/drive/files/${file.uid}/content`, { file_base64: dataUrl, mime_type: "application/pdf" })
        : await POST("v1/admin/drive/files", {
            folder_uid: file.folder_uid || undefined,
            files: [{ file_name: editedName, mime_type: "application/pdf", file_base64: dataUrl }],
          });
      return {
        status: result?.status,
        message: result?.message,
        doneMessage: mode === "overwrite" ? "บันทึกทับไฟล์เดิมแล้ว" : `บันทึกเป็นไฟล์ใหม่ "${editedName}" แล้ว`,
      };
    },
  };
}

/* ---------- สัญญาพัฒนาระบบ (dev-contracts) ---------- */

const DEV_API = "v1/admin/dev-contracts";

const base64ToBytes = async (base64) => {
  const response = await fetch(`data:application/pdf;base64,${base64}`);
  return new Uint8Array(await response.arrayBuffer());
};

// contract = แถวจาก list (stamp_layout: array = แบบเก่า, null = v2 + layout_version)
// stampImages = { stamp_uid: dataURL } ของคลังตราเดิมของสัญญา ใช้แปลง layout แบบเก่าเท่านั้น
export function devContractSource({ contract, stampImages }) {
  const fileName = contract.original_file_name || "contract.pdf";

  const LOAD_FAIL = "โหลดการจัดวางเดิมไม่สำเร็จ จึงไม่เปิดตัวแก้เพื่อป้องกันของเดิมถูกทับ กรุณาลองใหม่อีกครั้ง";

  // คืน layout เดิม หรือ null ถ้าสัญญาไม่เคยประทับ · ถ้าควรมี layout แต่ดึง/อ่านไม่ได้ให้ throw
  const fetchLayout = async () => {
    if (Array.isArray(contract.stamp_layout)) return contract.stamp_layout;
    if (contract.stamp_layout && typeof contract.stamp_layout === "object") return contract.stamp_layout;
    if (!contract.has_stamped && !contract.layout_version) return null;
    const result = await GET(`${DEV_API}/${contract.uid}/layout`, undefined, undefined, { silent: true });
    const layout = result?.status ? result.data?.stamp_layout : null;
    if (!layout || (!Array.isArray(layout) && typeof layout !== "object")) throw new Error(LOAD_FAIL);
    return layout;
  };

  // ภาพตราเดิมที่ยังไม่อยู่ในแคชของหน้า (ใช้แปลง layout แบบเก่า)
  const ensureStampImages = async (layout) => {
    const images = { ...(stampImages || {}) };
    const missing = [...new Set(layout.map((p) => p.stamp_uid))].filter((uid) => uid && !images[uid]);
    await Promise.all(missing.map(async (uid) => {
      const res = await GET(`${DEV_API}/stamps/${uid}/image`, undefined, undefined, { silent: true });
      if (res?.status && res.data?.data_url) images[uid] = res.data.data_url;
    }));
    return images;
  };

  return {
    uid: contract.uid,
    name: `${contract.project_name || "สัญญา"} · ${fileName}`,
    downloadName: `${pdfBase(fileName)}_stamped.pdf`,
    allowMerge: false, // layout เก็บเฉพาะหน้าของต้นฉบับ (ไม่เก็บ PDF ที่รวมเข้ามา)
    closeAfterSave: true,
    saveOptions: [
      {
        mode: "stamped", label: "บันทึกฉบับประทับตรา", icon: "fa-stamp",
        confirm: { icon: "info", title: "ยืนยัน", text: "บันทึก PDF ฉบับประทับตรา? ต้นฉบับจะไม่ถูกแก้ไข และฉบับประทับเดิม (ถ้ามี) จะถูกแทนที่", ok: "บันทึก" },
      },
    ],

    async load() {
      const result = await GET(`${DEV_API}/${contract.uid}/file`, { kind: "original" }, undefined, { silent: true });
      if (!result?.status || !result.data?.file_base64) return { error: result?.message || "โหลดไฟล์ต้นฉบับไม่สำเร็จ" };
      const bytes = await base64ToBytes(result.data.file_base64);

      return {
        bytes,
        restore: async (pages, nextId) => {
          let layout;
          try {
            layout = await fetchLayout();
            if (Array.isArray(layout)) return layout.length ? legacyLayoutToPages(layout, pages, await ensureStampImages(layout), nextId) : pages;
            if (layout?.version === 2) return restoreLayoutV2(layout, pages, nextId);
            if (layout) throw new Error("รูปแบบข้อมูลการจัดวางเดิมไม่รู้จัก");
            return pages;
          } catch (error) {
            throw new Error(error?.message === LOAD_FAIL ? LOAD_FAIL : `${LOAD_FAIL} (${error?.message || "ข้อมูลเสียหาย"})`);
          }
        },
      };
    },

    async save(mode, { bytes, pages }) {
      const dataUrl = await bytesToDataUrl(bytes, "application/pdf");
      // keepEmpty: ห้ามตัด array/ค่า 0 ทิ้งก่อนส่ง layout
      const result = await PUT(
        `${DEV_API}/${contract.uid}/stamped`,
        { file_base64: dataUrl, stamp_layout: buildLayoutV2(pages), page_count: pages.length },
        null,
        { keepEmpty: true }
      );
      return { status: result?.status, message: result?.message, doneMessage: "บันทึก PDF ฉบับประทับตราเรียบร้อย" };
    },
  };
}
